import { staffViewToken } from './staffTracking';
import { createConsumer } from '@rails/actioncable';

const PRESENCE_INTERVAL = 10000;
const USER_ACTIVITY_THRESHOLD = 30000; // 30 seconds - stop sending presence pings after this
const ACTIVITY_THROTTLE = 5000;
const RECONNECT_INTERVAL = 1000;
const INACTIVITY_CHECK_INTERVAL = 30000; // Check every 30 seconds

class BaseActionCableConnector {
  static isDisconnected = false;

  constructor(app, pubsubToken, websocketHost = '', auth = {}) {
    const websocketURL = websocketHost ? `${websocketHost}/cable` : undefined;

    this.consumer = createConsumer(websocketURL);
    this.subscription = this.consumer.subscriptions.create(
      {
        channel: 'RoomChannel',
        auth_client: auth.client,
        auth_token: auth['access-token'],
        pubsub_token: pubsubToken,
        account_id: app.$store.getters.getCurrentAccountId,
        user_id: app.$store.getters.getCurrentUserID,
      },
      {
        updatePresence(data) {
          this.perform('update_presence', data);
        },
        received: this.onReceived,
        disconnected: () => {
          if (this.disposed) return;
          BaseActionCableConnector.isDisconnected = true;
          this.onDisconnected();
          this.initReconnectTimer();
        },
      }
    );
    this.app = app;
    this.events = {};
    this.reconnectTimer = null;
    this.disposed = false;
    this.presenceTimer = null;
    this.lastUserInteraction = Date.now();
    this.lastPresenceSentAt = 0;
    this.wasInactive = false;
    this.inactivityTimer = null;
    this.isAValidEvent = () => true;

    this.maybeSendPresencePing = force => {
      if (this.disposed || document.visibilityState === 'hidden') return;
      if (this.app.$store.getters.getCurrentUserID && !document.hasFocus())
        return;
      const now = Date.now();
      if (!force && now - this.lastPresenceSentAt < ACTIVITY_THROTTLE) return;
      const routeId = this.app.$store.state?.route?.params?.conversationId;
      this.subscription.updatePresence({
        idle_ms: Math.max(0, now - this.lastUserInteraction),
        conversation_id: routeId || null,
        view_token: staffViewToken,
      });
      this.lastPresenceSentAt = now;
    };

    this.triggerPresenceInterval = () => {
      if (this.disposed) return;
      this.presenceTimer = setTimeout(() => {
        this.maybeSendPresencePing();
        this.triggerPresenceInterval();
      }, PRESENCE_INTERVAL);
    };
    this.triggerPresenceInterval();
    this.startInactivityMonitor();

    this.handleUserActivity = () => {
      this.lastUserInteraction = Date.now();
      this.maybeSendPresencePing();

      // If was inactive and now active, mark as active again
      if (this.wasInactive) {
        this.wasInactive = false;
        // console.log('[AUTO-AWAY] User became active again');
      }
    };

    window.addEventListener('mousemove', this.handleUserActivity);
    window.addEventListener('keydown', this.handleUserActivity);
    window.addEventListener('click', this.handleUserActivity, true);
    window.addEventListener('focus', this.handleUserActivity);
    this.handleBlur = () =>
      this.subscription.perform('pause_work', { view_token: staffViewToken });
    window.addEventListener('blur', this.handleBlur);
    window.addEventListener('scroll', this.handleUserActivity, true);
    window.addEventListener('touchstart', this.handleUserActivity, {
      passive: true,
    });

    // Update presence when tab becomes visible again
    this.handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        // console.log('[PRESENCE] Tab became visible, updating presence immediately');
        this.handleUserActivity();
      } else {
        this.subscription.perform('pause_work', { view_token: staffViewToken });
      }
    };
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
  }

  checkConnection() {
    if (this.disposed) return;
    const isConnectionActive = this.consumer.connection.isOpen();
    const isReconnected =
      BaseActionCableConnector.isDisconnected && isConnectionActive;
    if (isReconnected) {
      this.clearReconnectTimer();
      this.onReconnect();
      BaseActionCableConnector.isDisconnected = false;
    } else {
      this.initReconnectTimer();
    }
  }

  clearReconnectTimer = () => {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  };

  initReconnectTimer = () => {
    if (this.disposed) return;
    this.clearReconnectTimer();
    this.reconnectTimer = setTimeout(() => {
      this.checkConnection();
    }, RECONNECT_INTERVAL);
  };

  // eslint-disable-next-line class-methods-use-this
  onReconnect = () => {};

  // eslint-disable-next-line class-methods-use-this
  onDisconnected = () => {};

  startInactivityMonitor() {
    this.inactivityTimer = setInterval(() => {
      const now = Date.now();
      const inactiveDuration = now - this.lastUserInteraction;

      // If inactive for more than threshold and not already marked
      if (inactiveDuration > USER_ACTIVITY_THRESHOLD && !this.wasInactive) {
        this.wasInactive = true;
        // console.log(`[AUTO-AWAY] User inactive for ${Math.round(inactiveDuration / 1000)}s, notifying backend`);

        // Notify backend via ActionCable
        if (
          this.subscription &&
          typeof this.subscription.perform === 'function'
        ) {
          this.subscription.perform('set_away_status');
        }
      }
    }, INACTIVITY_CHECK_INTERVAL);
  }

  disconnect() {
    this.disposed = true;
    clearTimeout(this.presenceTimer);
    this.clearReconnectTimer();
    this.subscription.perform('pause_work', { view_token: staffViewToken });
    window.removeEventListener('scroll', this.handleUserActivity, true);
    window.removeEventListener('touchstart', this.handleUserActivity);
    if (this.inactivityTimer) {
      clearInterval(this.inactivityTimer);
      this.inactivityTimer = null;
    }
    document.removeEventListener(
      'visibilitychange',
      this.handleVisibilityChange
    );
    window.removeEventListener('mousemove', this.handleUserActivity);
    window.removeEventListener('keydown', this.handleUserActivity);
    window.removeEventListener('click', this.handleUserActivity, true);
    window.removeEventListener('focus', this.handleUserActivity);
    window.removeEventListener('blur', this.handleBlur);
    this.consumer.disconnect();
  }

  onReceived = ({ event, data } = {}) => {
    if (this.isAValidEvent(data)) {
      if (this.events[event] && typeof this.events[event] === 'function') {
        this.events[event](data);
      }
    }
  };
}

export default BaseActionCableConnector;
